import {
  createContext,
  FunctionComponent,
  ReactElement,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useQuery } from "@tanstack/react-query";

import Api from "src/Config";
import { textTruncate } from "src/helpers/truncateText";
import { Box, Text } from "@chakra-ui/react";

export const TransactionAssigneesContext = createContext(null);

type Props = {
  children: ReactElement;
};

const TransactionAssigneesProvider: FunctionComponent<Props> = ({
  children,
}) => {
  const [pageNumber, setPageNumber] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ["transaction_assignees"],
    staleTime: 30000,
    queryFn: async () => {
      const { data } = await Api.get(`tm/transactions/assigned/members`);

      const response = data.results.map(
        (res: {
          id: string;
          last_name: string;
          first_name: string;
          assigned_tm_count: string;
          email: string;
        }) => ({
          id: res.id,
          "transaction ID": res.id || "N/A",
          "assigned to": (
            <Box>
              <Text fontSize={"13px"}>
                {!res?.first_name?.toLowerCase() &&
                !res?.last_name?.toLowerCase()
                  ? "N/A"
                  : textTruncate(
                      `${res.first_name?.toLowerCase() || ""} ${
                        res.last_name?.toLowerCase() || ""
                      }`,
                      20,
                    )}
              </Text>
              <Text fontSize={"11px"} color={"#4F4F4F"}>
                {res.email}
              </Text>
            </Box>
          ),
          "number of transactions assigned": res.assigned_tm_count,
        }),
      );

      return { response, metadata: data?.metadata };
    },
  });

  const {
    data: assigneeData,
    isLoading: isLoadingAssignee,
    error: assigneeError,
    isFetching: isFetchingAssignee,
  } = useQuery({
    queryKey: ["assignee_transaction"],
    staleTime: 30000,
    queryFn: async () => {
      const url = `settings/tm/case-management/groups?page_size=100`;

      const { data } = await Api.get(`${url}`);

      const response = data?.results?.map(
        (res: {
          id: string;
          name: string;
          member_count: number;
          assigned_transactions_count: number;
          transaction_count: number;
          created_at: string;
          users: any;
        }) => ({
          id: res.id,
          team: `${textTruncate(`${res.name || ""}`, 20)}`,
          "number of team members": res?.member_count,
          members: res?.users,
        }),
      );

      return {
        response,
        metadata: data?.metadata,
      };
    },
  });

  useEffect(() => {
    if (data) {
      setPageCount(Math.ceil(data?.metadata.count / 10));
    }
  }, [data]);

  const contextValue = useMemo(
    () => ({
      isLoading,
      data,
      error,
      isFetching,
      pageCount,
      pageNumber,
      setPageNumber,
      assigneeData,
      isLoadingAssignee,
      assigneeError,
      isFetchingAssignee,
    }),
    [
      isLoading,
      data,
      error,
      isFetching,
      pageCount,
      pageNumber,
      setPageNumber,

      assigneeData,
      isLoadingAssignee,
      assigneeError,
      isFetchingAssignee,
    ],
  );
  return (
    <TransactionAssigneesContext.Provider value={contextValue}>
      {children}
    </TransactionAssigneesContext.Provider>
  );
};

export default TransactionAssigneesProvider;
